import { format } from 'date-fns';
import {
  AlertCircle,
  ArrowDownToLine,
  ArrowLeft,
  ArrowRightLeft,
  Building2,
  Calendar,
  Check,
  CheckCircle2,
  ChevronsUpDown,
  Clock,
  FileText,
  Folder,
  HandCoins,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  Save,
  Search,
  User,
  X,
} from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ColumnConfigDialog, type ColumnDefinition } from '@/components/shared/column-config-dialog';
import { CurrencyIcon } from '@/components/shared/currency-icon';
import { CustomerSearchCombobox } from '@/components/shared/customer-search-combobox';
import { TransactionDetailSheet } from '@/components/transactions/transaction-detail-sheet';
import { TransactionTable } from '@/components/transactions/transaction-table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { CurrencyInput } from '@/components/ui/currency-input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { useCompany } from '@/contexts/CompanyContext';
import type { CreatePromiseFormCustomer, CreatePromiseFormTransaction } from '@/hooks/useCreatePromiseData';
import {
  getConfirmationMethodId,
  useCreatePromiseCollectionCases,
  useCreatePromiseContacts,
  useCreatePromiseMutation,
} from '@/hooks/useCreatePromiseData';
import {
  buildCreatePromiseTransactionExportFilter,
  CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP,
  useCreatePromiseTransactionsPaginatedGraphQL,
} from '@/hooks/useCreatePromiseTransactionsGraphQL';
import { useExportAll } from '@/hooks/useExportAll';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import { useCompanyUsersList } from '@/hooks/useUsersData';
import { cn, formatDate } from '@/lib/utils';
import { formatCurrency, roundCurrency } from '@/lib/utils/formatters';
import { ExportProvider } from '@/providers/ExportProvider';
import type { ContactApi } from '@/types/api/customer-detail';
import type { ColumnInput } from '@/types/export';
import { Forms } from '@/types/export';
import type { ColumnVisibility, Transaction } from '@/types/transaction';

// Internal form state type
interface PromiseFormState {
  // Customer Information
  customerId: string;
  customerName: string;
  customerAccountNumber: string;
  contactId: string;
  contactName: string;
  customerEmail: string;
  customerPhone: string;

  // Promise Details
  promiseDate: Date | undefined;
  promisedAmount: string;
  confirmationMethod: string;
  assignedUserId?: string;
  caseId?: string; // Optional link to collection case

  // Invoice Selection
  selectedInvoices: Set<string>;
  invoiceAllocations: Record<string, number>;

  // Notes
  notes: string;
  sendConfirmation: boolean;
}

// Submit payload type
export interface PromiseFormData {
  customer: {
    id: string;
    name: string;
    accountNumber: string;
    email: string;
    phone: string;
  };
  promiseDate: Date;
  promisedAmount: number;
  confirmationMethod: string;
  selectedInvoices: string[];
  invoiceAllocations: Record<string, number>;
  notes: string;
  sendConfirmation: boolean;
}

function validatePromiseForm(
  selectedCustomer: CreatePromiseFormCustomer | null,
  selectedContact: { id: string } | null,
  formData: PromiseFormState,
  availableInvoices: CreatePromiseFormTransaction[],
  totalAllocated: number,
): string | null {
  if (!selectedCustomer) return 'Please select a customer';
  if (!selectedContact) return 'Please select a contact';
  if (!formData.promiseDate) return 'Please select a promise date';
  if (!formData.promisedAmount || parseFloat(formData.promisedAmount) <= 0) {
    return 'Please enter a valid promised amount';
  }
  if (!formData.confirmationMethod) return 'Please select a confirmation method';
  if (!formData.assignedUserId) return 'Please select an assigned user';
  if (formData.selectedInvoices.size === 0) return 'Please select at least one invoice';

  const promisedAmount = parseFloat(formData.promisedAmount);
  if (Math.abs(totalAllocated - promisedAmount) > 0.01) {
    return `Invoice allocations (${totalAllocated.toFixed(2)}) must equal promised amount (${promisedAmount.toFixed(2)})`;
  }

  const allocationError = validateInvoiceAllocations(formData, availableInvoices);
  if (allocationError) return allocationError;

  const totalOutstanding = roundCurrency(
    Array.from(formData.selectedInvoices).reduce((sum, invoiceId) => {
      const invoice = availableInvoices.find((inv) => inv.id === invoiceId);
      return sum + (invoice?.balanceRemaining ?? 0);
    }, 0),
  );

  if (roundCurrency(Math.abs(promisedAmount)) > roundCurrency(Math.abs(totalOutstanding))) {
    return `Total promised amount (${formatCurrency(promisedAmount)}) exceeds total outstanding (${formatCurrency(totalOutstanding)})`;
  }

  return null;
}

function validateInvoiceAllocations(
  formData: PromiseFormState,
  availableInvoices: CreatePromiseFormTransaction[],
): string | null {
  for (const invoiceId of Array.from(formData.selectedInvoices)) {
    const invoice = availableInvoices.find((inv) => inv.id === invoiceId);
    if (!invoice) continue;

    const allocated = formData.invoiceAllocations[invoiceId] ?? 0;
    const outstanding = invoice.balanceRemaining;

    if (outstanding !== 0 && allocated !== 0 && Math.sign(allocated) !== Math.sign(outstanding)) {
      return `Allocation for ${invoice.headerReference} has incorrect sign — must match the outstanding amount sign`;
    }
    if (roundCurrency(Math.abs(allocated)) > roundCurrency(Math.abs(outstanding))) {
      return `Allocation for ${invoice.headerReference} exceeds outstanding amount of ${formatCurrency(outstanding)}`;
    }
  }
  return null;
}

function resolvePrePopulatedCustomer(prePopulated: {
  customerId: string;
  customerName: string;
  accountNumber: string;
}): CreatePromiseFormCustomer {
  return {
    id: prePopulated.customerId,
    name: prePopulated.customerName,
    accountNumber: prePopulated.accountNumber,
    email: '',
    phone: '',
  };
}

function buildPrePopulatedTransactionData(transactions: CreatePromiseFormTransaction[]): {
  selectedIds: Set<string>;
  allocations: Record<string, number>;
  promisedAmount: string;
} {
  const selectedIds = new Set<string>(transactions.map((t) => t.id));
  const allocations: Record<string, number> = {};
  transactions.forEach((transaction) => {
    allocations[transaction.id] = transaction.balanceRemaining;
  });
  const totalAllocated = Object.values(allocations).reduce((sum, amount) => sum + amount, 0);
  return {
    selectedIds,
    allocations,
    promisedAmount: totalAllocated > 0 ? totalAllocated.toFixed(2) : '',
  };
}

function computeToggleInvoiceUpdates(
  formData: PromiseFormState,
  // biome-ignore lint/suspicious/noExplicitAny: pre-existing
  availableInvoices: any[],
  invoiceId: string,
  checked: boolean,
): Pick<PromiseFormState, 'selectedInvoices' | 'invoiceAllocations' | 'promisedAmount'> {
  const newSelected = new Set(formData.selectedInvoices);
  const newAllocations = { ...formData.invoiceAllocations };

  if (checked) {
    newSelected.add(invoiceId);
    const invoice = availableInvoices.find((inv) => inv.id === invoiceId);
    if (invoice) {
      newAllocations[invoiceId] = invoice.balanceRemaining;
    }
  } else {
    newSelected.delete(invoiceId);
    delete newAllocations[invoiceId];
  }

  const totalAllocated = Object.values(newAllocations).reduce((sum, amount) => sum + amount, 0);
  return {
    selectedInvoices: newSelected,
    invoiceAllocations: newAllocations,
    promisedAmount: totalAllocated > 0 ? totalAllocated.toFixed(2) : '',
  };
}

function validateAllocationChange(
  // biome-ignore lint/suspicious/noExplicitAny: pre-existing
  availableInvoices: any[],
  invoiceId: string,
  amount: number,
): string | null {
  const invoice = availableInvoices.find((inv) => inv.id === invoiceId);
  if (!invoice) return null;

  const outstanding = invoice.balanceRemaining;
  if (outstanding !== 0 && amount !== 0 && Math.sign(amount) !== Math.sign(outstanding)) {
    return `Amount sign must match the outstanding amount sign (${outstanding >= 0 ? 'positive' : 'negative'})`;
  }
  if (Math.abs(amount) > Math.abs(outstanding)) {
    return `Amount cannot exceed outstanding amount of ${formatCurrency(outstanding)}`;
  }
  return null;
}

function getContactPlaceholder(selectedCustomer: CreatePromiseFormCustomer | null, contactCount: number): string {
  if (!selectedCustomer) return 'Select a customer first';
  if (contactCount === 0) return 'No contacts available';
  return 'Select a contact';
}

const DEFAULT_CREATE_PROMISE_TX_COLUMNS: ColumnDefinition[] = [
  { id: 'referenceNumber', label: 'Reference Number', visible: true, order: 0, locked: true },
  { id: 'transactionDate', label: 'Transaction Date', visible: true, order: 1 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 2 },
  { id: 'transactionType', label: 'Type', visible: true, order: 3 },
  { id: 'amount', label: 'Gross', visible: true, order: 4 },
  { id: 'currency', label: 'Currency', visible: true, order: 5 },
  { id: 'outstandingAmount', label: 'Outstanding', visible: true, order: 6 },
  { id: 'promisedAmount', label: 'Promised Amount', visible: true, order: 7, locked: true },
  { id: 'status', label: 'Status', visible: true, order: 8 },
  { id: 'description', label: 'Description', visible: false, order: 9 },
  { id: 'accountCode', label: 'Account', visible: false, order: 10 },
  { id: 'netAmount', label: 'Net', visible: false, order: 11 },
  { id: 'vatAmount', label: 'Tax', visible: false, order: 12 },
  { id: 'subLedger', label: 'Sub Ledger', visible: false, order: 13 },
  { id: 'orderNumber', label: 'Order Number', visible: false, order: 14 },
  { id: 'createdBy', label: 'Created By', visible: false, order: 15 },
  { id: 'modifiedBy', label: 'Modified By', visible: false, order: 16 },
  { id: 'customerName', label: 'Customer Name', visible: false, order: 17 },
  { id: 'flags', label: 'Flags', visible: true, order: 18 },
  { id: 'onDispute', label: 'External Dispute', visible: false, order: 19 },
  { id: 'actions', label: 'Actions', visible: true, order: 20 },
];

const CreatePromisePage: React.FC = () => {
  const { selectedCompany } = useCompany();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCustomer, setSelectedCustomer] = useState<CreatePromiseFormCustomer | null>(null);
  const [availableContacts, setAvailableContacts] = useState<ContactApi[]>([]);
  const [selectedContact, setSelectedContact] = useState<ContactApi | null>(null);
  const [caseComboboxOpen, setCaseComboboxOpen] = useState(false);
  const createPromiseMutation = useCreatePromiseMutation();
  const { contacts: apiContacts } = useCreatePromiseContacts(selectedCustomer?.id);

  // Fetch active users for the Assigned User dropdown (company-scoped)
  const { data: activeUsers = [] } = useCompanyUsersList();

  const [formData, setFormData] = useState<PromiseFormState>({
    customerId: '',
    customerName: '',
    customerAccountNumber: '',
    contactId: '',
    contactName: '',
    customerEmail: '',
    customerPhone: '',
    promiseDate: undefined,
    promisedAmount: '',
    confirmationMethod: '',
    assignedUserId: undefined,
    selectedInvoices: new Set(),
    invoiceAllocations: {},
    notes: '',
    sendConfirmation: true,
  });

  // Get pre-populated data from location state
  const prePopulatedCustomer = location.state?.prePopulatedCustomer;
  const prePopulatedTransactions = location.state?.prePopulatedTransactions;

  // Search and column configuration for transactions table
  const [transactionSearchQuery, setTransactionSearchQuery] = useState('');
  const {
    columns: promiseTxCols,
    savePreferences: savePromiseTxPrefs,
    resetToDefaults: resetPromiseTxPrefs,
  } = useScreenColumnPreferences(
    UniqueTableNamesForColumnConfiguration.CreatePromiseTransactions,
    DEFAULT_CREATE_PROMISE_TX_COLUMNS,
  );
  const columnVisibility = useMemo(() => {
    const vis = {} as ColumnVisibility;
    promiseTxCols.forEach((col) => {
      vis[col.id as keyof ColumnVisibility] = col.visible;
    });
    return vis;
  }, [promiseTxCols]);

  // Sorting and pagination state for transactions table
  const [sortBy, setSortBy] = useState<string>('transactionDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [txCurrentPage, setTxCurrentPage] = useState(1);
  const [txItemsPerPage, setTxItemsPerPage] = useState(25);

  // Transaction detail sheet state
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [transactionDrawerOpen, setTransactionDrawerOpen] = useState(false);

  // Server-side GraphQL transactions (used when NOT pre-populated)
  const {
    transactions: graphqlTransactions,
    pagination: txPagination,
    totalOutstanding: graphqlTotalOutstanding,
  } = useCreatePromiseTransactionsPaginatedGraphQL(
    !prePopulatedTransactions ? formData.customerId || undefined : undefined,
    {
      page: txCurrentPage,
      pageSize: txItemsPerPage,
      search: transactionSearchQuery || undefined,
      sortBy,
      sortOrder,
    },
  );

  // Map GraphQL Transaction[] to CreatePromiseFormTransaction[] for form compatibility
  const apiTransactions: CreatePromiseFormTransaction[] = useMemo(
    () =>
      graphqlTransactions.map((tx): CreatePromiseFormTransaction => {
        const today = new Date();
        const dueDate = tx.dueDate ? new Date(tx.dueDate) : new Date();
        const daysOverdue = Math.max(0, Math.ceil((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)));
        return {
          id: String(tx.id),
          headerReference: tx.referenceNumber ?? `TXN-${tx.id}`,
          invoiceDate: tx.transactionDate ? new Date(tx.transactionDate) : new Date(),
          originalAmount: tx.amount ?? 0,
          balanceRemaining: tx.displayOutstandingAmount ?? tx.outstandingAmount ?? 0,
          daysOverdue,
          dueDate: tx.dueDate ? new Date(tx.dueDate) : null,
          transactionType: tx.transactionType ?? null,
          outstandingAmount: tx.outstandingAmount ?? null,
          currency: tx.currency ?? null,
          description: tx.description ?? null,
          accountCode: tx.accountCode ?? null,
          netAmount: tx.netAmount ?? null,
          vatAmount: tx.vatAmount ?? null,
          subLedger: tx.subLedger ?? null,
          orderNumber: tx.orderNumber ?? null,
          createdBy: tx.createdBy ?? null,
          modifiedBy: tx.modifiedBy ?? null,
          onDispute: tx.onDispute ?? null,
          status: tx.status ?? null,
        };
      }),
    [graphqlTransactions],
  );

  const availableInvoices: CreatePromiseFormTransaction[] = prePopulatedTransactions || apiTransactions;
  const { cases: availableCases } = useCreatePromiseCollectionCases(selectedCustomer?.id);

  // Load contacts when customer is selected or API contacts arrive
  useEffect(() => {
    if (!selectedCustomer) {
      setAvailableContacts([]);
      setSelectedContact(null);
      return;
    }
    if (apiContacts.length === 0) return;

    setAvailableContacts(apiContacts);
    const primaryContact = apiContacts.find((x) => x.isPrimary);
    if (primaryContact) {
      setSelectedContact(primaryContact);
      setFormData((prev) => ({
        ...prev,
        contactId: primaryContact.id,
        contactName: `${primaryContact.firstName} ${primaryContact.surname}`,
        customerEmail: primaryContact.email,
        customerPhone: primaryContact.phone,
      }));
    }
  }, [selectedCustomer, apiContacts]);

  // Handle pre-populated customer when page loads
  useEffect(() => {
    if (prePopulatedCustomer) {
      const customer = resolvePrePopulatedCustomer(prePopulatedCustomer);
      setSelectedCustomer(customer);
      setFormData((prev) => ({
        ...prev,
        customerId: customer.id,
        customerName: customer.name,
        customerAccountNumber: customer.accountNumber,
      }));
    }

    if (prePopulatedTransactions && prePopulatedTransactions.length > 0) {
      const txData = buildPrePopulatedTransactionData(prePopulatedTransactions);
      setFormData((prev) => ({
        ...prev,
        selectedInvoices: txData.selectedIds,
        invoiceAllocations: txData.allocations,
        promisedAmount: txData.promisedAmount,
      }));
    }
  }, [prePopulatedCustomer, prePopulatedTransactions]);

  // Handle customer selection
  const handleSelectCustomer = (customer: CreatePromiseFormCustomer) => {
    setSelectedCustomer(customer);
    setFormData((prev) => ({
      ...prev,
      customerId: customer.id,
      customerName: customer.name,
      customerAccountNumber: customer.accountNumber,
      contactId: '',
      contactName: '',
      customerEmail: '',
      customerPhone: '',
      caseId: undefined,
      assignedUserId: customer.creditControllerId ?? undefined,
      selectedInvoices: new Set<string>(),
      invoiceAllocations: {},
      promisedAmount: '',
    }));
    setTxCurrentPage(1);
  };

  // Handle contact selection
  const handleSelectContact = (contactId: string) => {
    const contact = availableContacts.find((c) => c.id === contactId);
    if (contact) {
      setSelectedContact(contact);
      setFormData((prev) => ({
        ...prev,
        contactId: contact.id,
        contactName: `${contact.firstName} ${contact.surname}`,
        customerEmail: contact.email,
        customerPhone: contact.phone,
      }));
    }
  };

  // Display transactions: server-side data is already filtered/sorted;
  // pre-populated path uses client-side filtering only.
  const displayTransactions: Transaction[] = useMemo(() => {
    if (prePopulatedTransactions) {
      // Client-side search for pre-populated transactions
      let filtered: CreatePromiseFormTransaction[] = availableInvoices;
      if (transactionSearchQuery.trim()) {
        const query = transactionSearchQuery.toLowerCase();
        filtered = availableInvoices.filter(
          (invoice) =>
            invoice.headerReference.toLowerCase().includes(query) ||
            format(invoice.invoiceDate, 'dd/MM/yyyy').includes(query) ||
            formatCurrency(invoice.originalAmount).toLowerCase().includes(query) ||
            formatCurrency(invoice.balanceRemaining).toLowerCase().includes(query) ||
            invoice.daysOverdue.toString().includes(query),
        );
      }
      // Map to Transaction[] for TransactionTable
      return filtered.map(
        (inv): Transaction => ({
          id: Number(inv.id),
          referenceNumber: inv.headerReference,
          transactionDate: inv.invoiceDate.toISOString(),
          customerId: Number(formData.customerId) || 0,
          customerName: selectedCustomer?.name ?? '',
          transactionType: (inv.transactionType as Transaction['transactionType']) ?? 'invoice',
          amount: inv.originalAmount,
          currency: inv.currency ?? 'GBP',
          status: (inv.status as Transaction['status']) ?? 'outstanding',
          description: inv.description ?? '',
          createdBy: inv.createdBy ?? '',
          modifiedBy: inv.modifiedBy ?? undefined,
          createdAt: inv.invoiceDate.toISOString(),
          dueDate: inv.dueDate?.toISOString() ?? undefined,
          outstandingAmount: inv.balanceRemaining,
          onDispute: inv.onDispute ?? false,
          hasDispute: inv.onDispute ?? false,
          accountCode: inv.accountCode ?? undefined,
          netAmount: inv.netAmount ?? undefined,
          vatAmount: inv.vatAmount ?? undefined,
          subLedger: inv.subLedger ?? undefined,
          orderNumber: inv.orderNumber ?? undefined,
          daysOverdue: inv.daysOverdue,
          ageingBucket: '',
          hasPromise: false,
          hasCase: false,
          allocationStatusCode: '',
          allocationStatusName: '',
          totalAllocated: 0,
          allocationCount: 0,
        }),
      );
    }
    return graphqlTransactions;
  }, [
    prePopulatedTransactions,
    availableInvoices,
    transactionSearchQuery,
    graphqlTransactions,
    formData.customerId,
    selectedCustomer?.name,
  ]);

  // Handle column configuration
  const handleColumnConfigApply = (columns: ColumnDefinition[]) => {
    savePromiseTxPrefs(columns);
  };

  // Server-side sort change handler
  const onSortChange = useCallback((newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setTxCurrentPage(1);
  }, []);

  // Handle transaction click - open detail sheet
  const handleTransactionClick = useCallback((transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setTransactionDrawerOpen(true);
  }, []);

  // Server-side XLSX export — build columns and filter
  const getExportFilter = useCallback((): Record<string, unknown> | undefined => {
    return buildCreatePromiseTransactionExportFilter(
      formData.customerId || undefined,
      transactionSearchQuery || undefined,
    );
  }, [formData.customerId, transactionSearchQuery]);

  const getExportSummary = useCallback((): string | undefined => {
    return transactionSearchQuery || undefined;
  }, [transactionSearchQuery]);

  const getVisibleExportColumns = useCallback((): ColumnInput[] => {
    return promiseTxCols
      .filter((col) => col.visible && col.id in CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP)
      .map((col) => ({
        name: CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP[col.id],
        displayName: col.label,
      }));
  }, [promiseTxCols]);

  const getAllExportColumns = useCallback((): ColumnInput[] => {
    return DEFAULT_CREATE_PROMISE_TX_COLUMNS.filter(
      (col) => col.id in CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP,
    ).map((col) => ({
      name: CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP[col.id],
      displayName: col.label,
    }));
  }, []);

  const { triggerExportAll: triggerExportCurrentView } = useExportAll({
    form: Forms.CreatePromiseTransactions,
    getColumns: getVisibleExportColumns,
    getFilter: getExportFilter,
    getSummary: getExportSummary,
    fileName: `create-promise-transactions-${selectedCustomer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const { triggerExportAll: triggerExportAllColumns } = useExportAll({
    form: Forms.CreatePromiseTransactions,
    getColumns: getAllExportColumns,
    getFilter: getExportFilter,
    getSummary: getExportSummary,
    fileName: `create-promise-transactions-all-columns-${selectedCustomer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const handleExportCurrentView = useCallback(() => {
    triggerExportCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  }, [triggerExportCurrentView]);

  const handleExportAllColumns = useCallback(() => {
    triggerExportAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  }, [triggerExportAllColumns]);

  // Derive the promise currency from the currently selected invoices
  const promiseCurrency = useMemo(() => {
    for (const invoiceId of Array.from(formData.selectedInvoices)) {
      const invoice = availableInvoices.find((inv) => inv.id === invoiceId);
      if (invoice?.currency) return invoice.currency;
    }
    return undefined;
  }, [formData.selectedInvoices, availableInvoices]);

  // Handle invoice selection
  const handleToggleInvoice = (invoiceId: string, checked: boolean) => {
    if (checked) {
      const invoice = availableInvoices.find((inv) => inv.id === invoiceId);
      if (invoice?.currency && promiseCurrency && invoice.currency !== promiseCurrency) {
        toast.error(
          `Cannot mix currencies. This promise uses ${promiseCurrency} but the selected transaction is in ${invoice.currency}.`,
        );
        return;
      }
    }
    const updates = computeToggleInvoiceUpdates(formData, availableInvoices, invoiceId, checked);
    setFormData({ ...formData, ...updates });
  };

  // Handle allocation amount change
  const handleAllocationChange = (invoiceId: string, amount: number) => {
    const error = validateAllocationChange(availableInvoices, invoiceId, amount);
    if (error) {
      toast.error(error);
      return;
    }

    const newAllocations = {
      ...formData.invoiceAllocations,
      [invoiceId]: amount,
    };
    const totalAllocated = Object.values(newAllocations).reduce((sum, amt) => sum + amt, 0);
    setFormData({
      ...formData,
      invoiceAllocations: newAllocations,
      promisedAmount: totalAllocated > 0 ? totalAllocated.toFixed(2) : '',
    });
  };

  // Calculate total promised amount from allocations
  const calculateTotalFromAllocations = () => {
    return roundCurrency(Object.values(formData.invoiceAllocations).reduce((sum, amount) => sum + amount, 0));
  };

  const handleSave = () => {
    if (createPromiseMutation.isPending) return;
    const validationError = validatePromiseForm(
      selectedCustomer,
      selectedContact,
      formData,
      availableInvoices,
      calculateTotalFromAllocations(),
    );

    if (validationError) {
      toast.error(validationError);
      return;
    }

    const linkedInvoices = Array.from(formData.selectedInvoices).map((invoiceId) => {
      const invoice = availableInvoices.find((inv) => inv.id === invoiceId);
      const txId = parseInt(invoiceId, 10);
      return {
        transactionId: isNaN(txId) ? 0 : txId,
        invoiceNumber: invoice?.headerReference ?? '',
        invoiceDate: invoice ? format(invoice.invoiceDate, 'yyyy-MM-dd') : undefined,
        originalAmount: invoice?.originalAmount ?? 0,
        promisedAmount: formData.invoiceAllocations[invoiceId] || 0,
      };
    });

    const apiRequest = {
      customerId: parseInt(selectedCustomer!.id, 10),
      caseId: formData.caseId ? parseInt(formData.caseId, 10) : undefined,
      contactId: selectedContact?.id ? parseInt(selectedContact.id, 10) : undefined,
      promiseDate: format(formData.promiseDate!, 'yyyy-MM-dd'),
      promisedAmount: parseFloat(formData.promisedAmount),
      confirmationMethodId: getConfirmationMethodId(formData.confirmationMethod),
      assignedUserId: parseInt(formData.assignedUserId!, 10),
      notes: formData.notes || undefined,
      sendConfirmation: formData.sendConfirmation,
      linkedInvoices,
    };

    createPromiseMutation.mutate(apiRequest, {
      onSuccess: () => {
        toast.success('Promise to pay created successfully');
        navigate('/collections/promises');
      },
      onError: (error) => {
        toast.error(`Failed to create promise: ${error.message}`);
      },
    });
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/collections/promises');
  };

  const totalAllocated = calculateTotalFromAllocations();

  const contactPlaceholder = getContactPlaceholder(selectedCustomer, availableContacts.length);

  const caseDisplayText = formData.caseId
    ? (availableCases.find((c) => c.id === formData.caseId)?.caseNumber ?? 'None')
    : 'None';

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b bg-background sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <Button variant="ghost" size="sm" onClick={handleCancel} className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
              </div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <HandCoins className="h-6 w-6 text-primary" />
                Create New Promise
              </h1>
              <p className="text-muted-foreground mt-1">Record a new payment commitment from a customer</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={createPromiseMutation.isPending}>
                {createPromiseMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {createPromiseMutation.isPending ? 'Creating...' : 'Create Promise'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
        <div className="px-6 pt-4 border-b shrink-0">
          <TabsList className="w-full justify-start">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="audit">Audit Log</TabsTrigger>
          </TabsList>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Overview Tab */}
          <TabsContent value="overview" className="px-6 py-4 space-y-6 m-0">
            {/* Two-Column Layout: Customer Information (Left) and Promise Details (Right) */}
            <div className="grid gap-6 lg:grid-cols-[1fr_1.5fr]">
              {/* Customer Information Panel - LEFT */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    Customer Information
                  </CardTitle>
                  <CardDescription>Select the customer and contact making the promise</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* First Row: Customer and Contact Name */}
                  <div className="grid grid-cols-2 gap-x-4">
                    {/* Customer Dropdown */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        Customer *
                      </Label>
                      {prePopulatedCustomer ? (
                        <div className="space-y-2">
                          <p className="text-sm font-semibold">
                            {formData.customerName} ({formData.customerAccountNumber})
                          </p>
                          <Badge variant="secondary" className="text-xs">
                            Pre-selected
                          </Badge>
                        </div>
                      ) : (
                        <CustomerSearchCombobox
                          selectedCustomer={selectedCustomer}
                          onSelectCustomer={handleSelectCustomer}
                        />
                      )}
                    </div>

                    {/* Contact Name Dropdown */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <User className="h-3 w-3" />
                        Contact Name *
                      </Label>
                      <Select
                        value={formData.contactId}
                        onValueChange={handleSelectContact}
                        disabled={!selectedCustomer || availableContacts.length === 0}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder={contactPlaceholder} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableContacts.map((contact) => (
                            <SelectItem key={contact.id} value={contact.id}>
                              {contact.firstName} {contact.surname}
                              {contact.isPrimary && ' (Primary)'}
                              {contact.jobTitle && ` - ${contact.jobTitle}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Second Row: Email Address and Phone Number */}
                  <div className="grid grid-cols-2 gap-x-4">
                    {/* Email Address (Auto-populated) */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        Email Address
                      </Label>
                      <Input
                        value={formData.customerEmail}
                        readOnly
                        disabled
                        className="h-9 bg-muted"
                        placeholder="Select a contact to view email"
                      />
                    </div>

                    {/* Phone Number (Auto-populated) */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        Phone Number
                      </Label>
                      <Input
                        value={formData.customerPhone}
                        readOnly
                        disabled
                        className="h-9 bg-muted"
                        placeholder="Select a contact to view phone"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Promise Details Panel - RIGHT */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HandCoins className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    Promise Details
                  </CardTitle>
                  <CardDescription>Enter the payment commitment details</CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Three-column grid layout for first row, two-column for second row */}
                  <div className="space-y-4">
                    {/* First Row: Promise Date, Promised Amount, Assigned User */}
                    <div className="grid grid-cols-3 gap-x-4">
                      {/* Promise Date */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Promise Date *
                        </Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                'w-full justify-start text-left font-normal h-9 text-sm',
                                !formData.promiseDate && 'text-muted-foreground',
                              )}
                            >
                              <Calendar className="mr-2 h-3 w-3" />
                              {formData.promiseDate ? formatDate(formData.promiseDate) : 'Pick date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <CalendarComponent
                              mode="single"
                              selected={formData.promiseDate}
                              onSelect={(date) => setFormData({ ...formData, promiseDate: date })}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Promised Amount */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <CurrencyIcon
                            currencyCode={promiseCurrency || selectedCompany?.currency}
                            className="h-3 w-3"
                          />
                          Promised Amount *
                        </Label>
                        <Input
                          type="text"
                          placeholder="0.00"
                          value={
                            formData.promisedAmount
                              ? formatCurrency(
                                  parseFloat(formData.promisedAmount),
                                  promiseCurrency || selectedCompany?.currency || 'GBP',
                                )
                              : ''
                          }
                          readOnly
                          disabled
                          className="h-9 bg-muted font-medium"
                          title="This amount is automatically calculated from selected transactions"
                        />
                        <p className="text-xs text-muted-foreground">Auto-calculated from transactions</p>
                      </div>

                      {/* Assigned User */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <User className="h-3 w-3" />
                          Assigned User *
                        </Label>
                        {!selectedCustomer ? (
                          <Button
                            variant="outline"
                            className="w-full justify-between h-9 font-normal text-muted-foreground"
                            disabled
                          >
                            Select a customer first
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        ) : (
                          <Select
                            value={formData.assignedUserId || ''}
                            onValueChange={(value) => setFormData({ ...formData, assignedUserId: value })}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Select user..." />
                            </SelectTrigger>
                            <SelectContent>
                              {activeUsers.map((user) => (
                                <SelectItem key={user.id} value={user.id}>
                                  <div className="flex items-center gap-2">
                                    <Avatar className="h-5 w-5">
                                      <AvatarImage src={user.avatar} alt={`${user.firstName} ${user.lastName}`} />
                                      <AvatarFallback className="text-[10px]">
                                        {user.firstName[0]}
                                        {user.lastName[0]}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span>
                                      {user.firstName} {user.lastName}
                                    </span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>

                    {/* Second Row: Confirmation Method and Collection Case */}
                    <div className="grid grid-cols-2 gap-x-4">
                      {/* Confirmation Method */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Confirmation Method *
                        </Label>
                        <Select
                          value={formData.confirmationMethod}
                          onValueChange={(value) => setFormData({ ...formData, confirmationMethod: value })}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select method" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Phone">Phone</SelectItem>
                            <SelectItem value="Email">Email</SelectItem>
                            <SelectItem value="Portal">Portal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Related Collection Case - Right column */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Folder className="h-3 w-3" />
                          Collection Case (Optional)
                        </Label>
                        {!selectedCustomer ? (
                          <Button
                            variant="outline"
                            className="w-full justify-between h-9 font-normal text-muted-foreground"
                            disabled
                          >
                            Select a customer first
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        ) : (
                          <Popover open={caseComboboxOpen} onOpenChange={setCaseComboboxOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={caseComboboxOpen}
                                className="w-full justify-between h-9 font-normal"
                              >
                                {caseDisplayText}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                              <Command>
                                <CommandInput placeholder="Search cases..." />
                                <CommandList>
                                  <CommandEmpty>No cases found.</CommandEmpty>
                                  <CommandGroup>
                                    <CommandItem
                                      value="none"
                                      onSelect={() => {
                                        setFormData({ ...formData, caseId: undefined });
                                        setCaseComboboxOpen(false);
                                      }}
                                    >
                                      <Check
                                        className={cn('mr-2 h-4 w-4', !formData.caseId ? 'opacity-100' : 'opacity-0')}
                                      />
                                      None
                                    </CommandItem>
                                    {availableCases.map((caseItem) => (
                                      <CommandItem
                                        key={caseItem.id}
                                        value={caseItem.caseNumber}
                                        onSelect={() => {
                                          setFormData({ ...formData, caseId: caseItem.id });
                                          setCaseComboboxOpen(false);
                                        }}
                                      >
                                        <Check
                                          className={cn(
                                            'mr-2 h-4 w-4',
                                            formData.caseId === caseItem.id ? 'opacity-100' : 'opacity-0',
                                          )}
                                        />
                                        {caseItem.caseNumber}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    </div>

                    {/* Send Confirmation - Hidden */}
                    {/* <div className="pt-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="send-confirmation"
                          checked={formData.sendConfirmation}
                          onCheckedChange={(checked) =>
                            setFormData({ ...formData, sendConfirmation: checked as boolean })
                          }
                        />
                        <Label htmlFor="send-confirmation" className="text-sm font-normal cursor-pointer">
                          Send confirmation email to customer
                        </Label>
                      </div>
                    </div> */}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Promise Notes Panel - Full Width at Bottom */}
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-[250px_1fr] gap-6 items-start">
                  {/* Left side: Title and Description */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      <h3 className="text-lg font-semibold">Promise Notes</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">Add any additional information about this promise</p>
                  </div>

                  {/* Right side: Textarea */}
                  <Textarea
                    placeholder="Enter notes about the promise, customer conversation, or any special conditions..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="resize-none"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Transactions Panel - Full Width at Bottom */}
            <Card className="min-w-0">
              <CardHeader>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <ArrowRightLeft className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <CardTitle className="text-lg">Transactions</CardTitle>
                    {selectedCustomer && (
                      <Badge variant="secondary" className="ml-2">
                        {txPagination.totalItems}
                      </Badge>
                    )}
                  </div>
                  {selectedCustomer && (
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="search"
                          placeholder="Quick search..."
                          value={transactionSearchQuery}
                          onChange={(e) => {
                            setTransactionSearchQuery(e.target.value);
                            setTxCurrentPage(1);
                          }}
                          className="pl-8 h-9 w-[200px]"
                        />
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="icon" className="h-9 w-9" title="Export to Excel">
                            <ArrowDownToLine className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={handleExportCurrentView}>Export Current View</DropdownMenuItem>
                          <DropdownMenuItem onClick={handleExportAllColumns}>Export All Columns</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <ColumnConfigDialog
                        columns={promiseTxCols}
                        onApply={handleColumnConfigApply}
                        onReset={resetPromiseTxPrefs}
                      />
                    </div>
                  )}
                </div>
                {!selectedCustomer && <CardDescription>Select a customer first to view transactions</CardDescription>}
              </CardHeader>
              {selectedCustomer && (
                <CardContent className="overflow-x-auto">
                  <TransactionTable
                    transactions={displayTransactions}
                    columnVisibility={columnVisibility}
                    orderedColumns={promiseTxCols}
                    onViewTransaction={handleTransactionClick}
                    showCustomerColumn={false}
                    displayAmounts={true}
                    enableSelection={true}
                    selectedRows={new Set(Array.from(formData.selectedInvoices).map((x) => Number(x)))}
                    onSelectRow={(id, checked) => handleToggleInvoice(String(id), checked)}
                    onSelectAll={(checked) => {
                      if (checked) {
                        const allIds = new Set(displayTransactions.map((x) => String(x.id)));
                        const newAllocations = { ...formData.invoiceAllocations };
                        displayTransactions.forEach((x) => {
                          const txId = String(x.id);
                          if (!(txId in newAllocations)) {
                            newAllocations[txId] = x.displayOutstandingAmount ?? x.outstandingAmount ?? 0;
                          }
                        });
                        const total = Object.values(newAllocations).reduce((sum, amt) => sum + amt, 0);
                        setFormData({
                          ...formData,
                          selectedInvoices: allIds,
                          invoiceAllocations: newAllocations,
                          promisedAmount: total > 0 ? total.toFixed(2) : '',
                        });
                      } else {
                        setFormData({
                          ...formData,
                          selectedInvoices: new Set(),
                          invoiceAllocations: {},
                          promisedAmount: '',
                        });
                      }
                    }}
                    currentPage={txCurrentPage}
                    pageSize={txItemsPerPage}
                    totalItems={prePopulatedTransactions ? undefined : txPagination.totalItems}
                    onPageChange={setTxCurrentPage}
                    onPageSizeChange={(size) => {
                      setTxItemsPerPage(size);
                      setTxCurrentPage(1);
                    }}
                    onSortChange={prePopulatedTransactions ? undefined : onSortChange}
                    renderActions={(transaction) => (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTransactionClick(transaction);
                        }}
                      >
                        View
                      </Button>
                    )}
                    renderPromisedAmount={(transaction) => {
                      const txId = String(transaction.id);
                      const isSelected = formData.selectedInvoices.has(txId);
                      if (isSelected) {
                        const allocatedAmount = formData.invoiceAllocations[txId] || 0;
                        return (
                          <CurrencyInput
                            currencyCode={transaction.currency || selectedCompany?.currency || 'GBP'}
                            value={allocatedAmount}
                            onChange={(val) => handleAllocationChange(txId, val ?? 0)}
                            min={0}
                            max={transaction.displayOutstandingAmount ?? transaction.outstandingAmount ?? undefined}
                            sign="positive"
                            className="w-32 text-right h-8"
                          />
                        );
                      }
                      return <span className="text-muted-foreground">-</span>;
                    }}
                  />

                  <Separator className="my-4" />
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Total Promised Amount:</span>
                      <span className="text-xl font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(totalAllocated, promiseCurrency || selectedCompany?.currency || 'GBP')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Total Outstanding:</span>
                      <span className="text-xl font-bold text-red-600 dark:text-red-400">
                        {formatCurrency(
                          prePopulatedTransactions
                            ? availableInvoices.reduce((sum, inv) => sum + inv.balanceRemaining, 0)
                            : graphqlTotalOutstanding,
                          promiseCurrency || selectedCompany?.currency || 'GBP',
                        )}
                      </span>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          </TabsContent>

          {/* Activity Tab - Consolidated view of Activity, Notes, and Communications */}
          <TabsContent value="activity" className="px-6 py-4 space-y-6 m-0">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">Activity Timeline</CardTitle>
                    <CardDescription>Add notes and view activity history for this promise</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add New Note Section */}
                <div className="space-y-3 p-4 border rounded-lg bg-purple-50/50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-900">
                  <Label htmlFor="promiseNotes" className="text-sm font-medium flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Promise Notes
                  </Label>
                  <Textarea
                    id="promiseNotes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Enter notes about the promise, customer conversation, or any special conditions..."
                    rows={6}
                    className="resize-none"
                  />
                  <p className="text-xs text-muted-foreground">
                    These notes will be visible in the promise details and activity history
                  </p>
                </div>

                {/* Note Preview */}
                {formData.notes && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Note Preview
                      </Label>
                      <div className="bg-muted p-4 rounded-lg border">
                        <p className="text-sm whitespace-pre-wrap leading-relaxed">{formData.notes}</p>
                      </div>
                    </div>
                  </>
                )}

                {/* Information Banner */}
                <Separator />
                <div className="flex items-start gap-3 p-4 border rounded-lg bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
                  <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Activity History</p>
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      Once this promise is created, all activities, notes, and communications will be tracked and
                      displayed here in a unified timeline. This includes emails sent, calls logged, status changes, and
                      any notes added by team members.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit" className="px-6 py-4 space-y-4 m-0">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Audit Log
                </CardTitle>
                <CardDescription>
                  Complete chronological history of all promise modifications and changes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-muted-foreground">
                  <Clock className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-base font-medium mb-2">No Audit History Yet</p>
                  <p className="text-sm max-w-md mx-auto">
                    Once this promise is created, all modifications and changes will be tracked here. This includes
                    status changes, amount updates, date modifications, and field edits.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </div>
      </Tabs>

      {/* Transaction Details Drawer */}
      <TransactionDetailSheet
        open={transactionDrawerOpen}
        onOpenChange={setTransactionDrawerOpen}
        transaction={selectedTransaction}
      />
    </div>
  );
};

function CreatePromisePageWrapper() {
  return (
    <ExportProvider>
      <CreatePromisePage />
    </ExportProvider>
  );
}

export default CreatePromisePageWrapper;
